import React from 'react'
import clsx from 'clsx'

type BadgeVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'outline' | 'pro' | 'new' | 'streak' | 'achievement' | 'rank' | 'gaming'
type BadgeSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const getVariantClasses = (variant: BadgeVariant) => {
  const variants = {
    default: 'bg-slate-600 text-white border-slate-600',
    primary: 'bg-orange-500 text-white border-orange-500',
    secondary: 'bg-slate-500 text-white border-slate-500',
    success: 'bg-green-500 text-white border-green-500',
    warning: 'bg-yellow-500 text-slate-900 border-yellow-500',
    danger: 'bg-red-500 text-white border-red-500',
    info: 'bg-blue-500 text-white border-blue-500',
    outline: 'bg-transparent text-white border-slate-400 hover:bg-slate-600',
    pro: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-500 shadow-glow',
    new: 'bg-gradient-to-r from-green-400 to-blue-500 text-white border-green-400 animate-pulse',
    streak: 'bg-gradient-to-r from-yellow-400 to-orange-500 text-slate-900 border-yellow-400 shadow-glow',
    achievement: 'bg-gradient-to-r from-yellow-500 to-amber-600 text-slate-900 border-yellow-500',
    rank: 'bg-gradient-to-r from-blue-400 to-indigo-500 text-white border-blue-400',
    gaming: 'bg-gradient-to-r from-orange-500 to-red-500 text-white border-orange-500 shadow-glow animate-pulse',
  }
  return variants[variant]
}

const getSizeClasses = (size: BadgeSize) => {
  const sizes = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-1.5 text-base',
    xl: 'px-5 py-2 text-lg',
  }
  return sizes[size]
}

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant
  size?: BadgeSize
  dot?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  children: React.ReactNode
}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant = 'default', size = 'md', dot = false, leftIcon, rightIcon, children, ...props }, ref) => {
    const iconSize = size === 'xs' ? 'h-3 w-3' : size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : size === 'lg' ? 'h-5 w-5' : 'h-6 w-6'
    
    return (
      <div
        ref={ref}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border font-medium transition-all duration-200',
          getVariantClasses(variant),
          getSizeClasses(size),
          className
        )}
        {...props}
      >
        {dot && (
          <div
            className={clsx(
              'rounded-full',
              size === 'xs' ? 'h-1 w-1' : size === 'sm' ? 'h-1.5 w-1.5' : size === 'md' ? 'h-2 w-2' : size === 'lg' ? 'h-2.5 w-2.5' : 'h-3 w-3',
              variant === 'outline' ? 'bg-white' : 'bg-current opacity-80'
            )}
          />
        )}
        {leftIcon && (
          <span className={iconSize}>
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span className={iconSize}>
            {rightIcon}
          </span>
        )}
      </div>
    )
  }
)

Badge.displayName = 'Badge'

export { Badge }